import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  Calendar, ChevronRight, Layers, Users, UserCheck, ClipboardList,
  AlertTriangle, MessageSquare, ArrowRight, CalendarPlus, Megaphone,
} from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ScheduleSessionForm } from '@/features/Trainer/ScheduleSessionForm';
import { AnnouncementComposer } from '@/features/Trainer/AnnouncementComposer';
import { TrainerCard } from '@/features/TrainerLayout/primitives/TrainerCard';
import { useTrainerDashboard, useBatchOverview } from '@/lib/hooks/useTrainer';
import { useBatchCurriculum } from '@/lib/hooks/useLms';
import type { DashboardBatch, DashboardSession } from '@/lib/api/trainer';
import { cn } from '@/lib/utils';

interface BatchOpt { id: string; name: string }

export const Route = createFileRoute('/trainer/dashboard')({
  component: TrainerDashboardPage,
});

function greeting(name: string) {
  const h = new Date().getHours();
  const time = h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening';
  const first = name.split(' ')[0] || name;
  return `Good ${time}, ${first}`;
}

// KPI tile — matches the trainer Analytics page for visual consistency.
function KpiCard({ label, value, sub, icon, color, iconBg, alert }: {
  label: string; value: number | string; sub?: React.ReactNode;
  icon: React.ReactNode; color: string; iconBg: string; alert?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-[24px] border bg-white p-4 text-left',
        alert ? 'border-[#fecaca] bg-gradient-to-b from-white to-[#fef2f2]' : 'border-[#ececec]',
      )}
    >
      <div className="mb-2.5 flex items-center gap-2.5">
        <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${iconBg}`}>{icon}</div>
        <span className="text-[10.5px] font-bold uppercase tracking-widest text-[#9ca3af]">{label}</span>
      </div>
      <div className="flex items-end gap-2">
        <span className={`text-3xl font-bold leading-none ${color}`}>{value}</span>
      </div>
      {sub && <div className="mt-2 text-[11.5px] text-[#6b7280]">{sub}</div>}
    </div>
  );
}

// ── primary actions (batch-scoped, launched from the dashboard) ────────────────

function BatchPicker({ batches, value, onChange }: {
  batches: BatchOpt[]; value: string; onChange: (v: string) => void;
}) {
  return (
    <div className="mb-4">
      <label className="mb-1.5 block text-[12px] font-semibold text-muted-foreground">Batch</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-full"><SelectValue placeholder="Select a batch…" /></SelectTrigger>
        <SelectContent>
          {batches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

// Loads the selected batch's curriculum + sessions, then renders the real form.
function ScheduleBody({ batchId, onSuccess }: { batchId: string; onSuccess: () => void }) {
  const { data: curriculum, isLoading: cl } = useBatchCurriculum(batchId);
  const { data: overview, isLoading: ol } = useBatchOverview(batchId);
  if (cl || ol) return <div className="py-6 text-center text-sm text-muted-foreground">Loading curriculum…</div>;
  return (
    <ScheduleSessionForm
      batchId={batchId}
      modules={curriculum?.modules ?? []}
      sessions={overview?.upcoming_sessions ?? []}
      forceOpen
      onSuccess={onSuccess}
    />
  );
}

function ScheduleSessionDialog({ batches, open, onOpenChange }: {
  batches: BatchOpt[]; open: boolean; onOpenChange: (v: boolean) => void;
}) {
  const [batchId, setBatchId] = useState(batches[0]?.id ?? '');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl overflow-visible"
        onInteractOutside={(e) => {
          if ((e.target as Element)?.closest?.('[data-radix-popper-content-wrapper]')) e.preventDefault();
        }}
      >
        <DialogHeader><DialogTitle>Schedule session</DialogTitle></DialogHeader>
        <BatchPicker batches={batches} value={batchId} onChange={setBatchId} />
        {batchId
          ? <ScheduleBody batchId={batchId} onSuccess={() => onOpenChange(false)} />
          : <p className="py-4 text-center text-sm text-muted-foreground">Pick a batch to continue.</p>}
      </DialogContent>
    </Dialog>
  );
}

function AnnounceDialog({ batches, open, onOpenChange }: {
  batches: BatchOpt[]; open: boolean; onOpenChange: (v: boolean) => void;
}) {
  const [batchId, setBatchId] = useState(batches[0]?.id ?? '');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Post announcement</DialogTitle></DialogHeader>
        <BatchPicker batches={batches} value={batchId} onChange={setBatchId} />
        {batchId
          ? <AnnouncementComposer batchId={batchId} onSuccess={() => onOpenChange(false)} />
          : <p className="py-4 text-center text-sm text-muted-foreground">Pick a batch to continue.</p>}
      </DialogContent>
    </Dialog>
  );
}

// ── attention feed ────────────────────────────────────────────────────────────

type FeedKind = 'risk' | 'grade' | 'questions';
interface FeedItem {
  key: string;
  kind: FeedKind;
  score: number;
  title: string;
  sub: string;
  cta: string;
  onClick: () => void;
}

const FEED_STYLE: Record<FeedKind, { sev: string; iconBg: string; icon: React.ReactNode }> = {
  risk:      { sev: 'bg-red-500',    iconBg: 'bg-red-50',    icon: <AlertTriangle className="h-4 w-4 text-red-500" /> },
  grade:     { sev: 'bg-violet-500', iconBg: 'bg-violet-50',  icon: <ClipboardList className="h-4 w-4 text-violet-600" /> },
  questions: { sev: 'bg-blue-500',   iconBg: 'bg-blue-50',    icon: <MessageSquare className="h-4 w-4 text-blue-600" /> },
};

function FeedRow({ item }: { item: FeedItem }) {
  const s = FEED_STYLE[item.kind];
  return (
    <button
      type="button"
      onClick={item.onClick}
      className="flex w-full items-center gap-3 border-b border-border/60 py-3 pr-4 text-left transition-colors last:border-0 hover:bg-muted/40"
    >
      <span className={`h-full min-h-[38px] w-1 flex-none rounded-r ${s.sev}`} />
      <span className={`flex h-9 w-9 flex-none items-center justify-center rounded-lg ${s.iconBg}`}>{s.icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13.5px] font-semibold text-foreground">{item.title}</span>
        <span className="block truncate text-[12px] text-muted-foreground">{item.sub}</span>
      </span>
      <span className="flex flex-none items-center gap-1 text-[12.5px] font-bold text-[#ff5a1f]">
        {item.cta} <ArrowRight className="h-3.5 w-3.5" />
      </span>
    </button>
  );
}

// ── today's schedule ────────────────────────────────────────────────────────────

function ScheduleItem({ session, isNext, onClick }: {
  session: DashboardSession; isNext: boolean; onClick: () => void;
}) {
  const d = new Date(session.scheduled_at);
  const hour = d.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true }).replace(/\s?[AP]M/i, '');
  const mer = d.toLocaleTimeString('en-US', { hour12: true }).match(/[AP]M/i)?.[0] ?? '';
  const min = d.getMinutes().toString().padStart(2, '0');
  return (
    <button
      type="button"
      onClick={onClick}
      className="grid w-full grid-cols-[52px_1fr] gap-3 px-5 py-2.5 text-left transition-colors hover:bg-muted/40"
    >
      <div className="pt-0.5 text-right text-[12px] font-bold tabular-nums text-foreground">
        {hour}:{min}<span className="block text-[9.5px] font-semibold text-muted-foreground">{mer}</span>
      </div>
      <div className={cn('border-l-2 pl-3.5', isNext ? 'border-green-500' : 'border-border')}>
        <p className="truncate text-[13.5px] font-medium text-foreground">{session.title}</p>
        <p className="truncate text-[11.5px] text-muted-foreground">{session.batch_name} · {session.org_name}</p>
        {isNext && (
          <span className="mt-1 inline-block rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-wide text-green-600">
            Up next
          </span>
        )}
      </div>
    </button>
  );
}

// ── batch comparison table ────────────────────────────────────────────────────

type FlatBatch = DashboardBatch & { orgName: string };
type BatchSort = 'attention' | 'progress' | 'attendance' | 'name';

function Chip({ n, tone }: { n: number; tone: 'risk' | 'warn' }) {
  if (n === 0) return <span className="inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-full bg-muted px-1.5 text-[11.5px] font-bold tabular-nums text-muted-foreground">0</span>;
  const cls = tone === 'risk'
    ? 'border-red-200 bg-red-50 text-red-600'
    : 'border-amber-200 bg-amber-50 text-amber-700';
  return <span className={`inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-full border px-1.5 text-[11.5px] font-bold tabular-nums ${cls}`}>{n}</span>;
}

// ── page ────────────────────────────────────────────────────────────────────────

export function TrainerDashboardPage() {
  const { data, isLoading, isError, refetch } = useTrainerDashboard();
  const navigate = useNavigate();
  const [sort, setSort] = useState<BatchSort>('attention');
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [announceOpen, setAnnounceOpen] = useState(false);

  const goToOverview = (batchId: string) =>
    navigate({ to: '/trainer/batches/$batchId/overview', params: { batchId } });
  const goToStudents = (batchId: string) =>
    navigate({ to: '/trainer/batches/$batchId/students', params: { batchId } });

  const flatBatches = useMemo<FlatBatch[]>(
    () => (data?.orgs ?? []).flatMap(o => o.batches.map(b => ({ ...b, orgName: o.org_name }))),
    [data],
  );

  const feed = useMemo<FeedItem[]>(() => {
    if (!data) return [];
    const items: FeedItem[] = [];
    flatBatches.forEach(b => {
      if (b.at_risk_count > 0) {
        items.push({
          key: `risk-${b.id}`, kind: 'risk', score: 1000 + b.at_risk_count,
          title: `${b.at_risk_count} student${b.at_risk_count === 1 ? '' : 's'} below 20% in ${b.name}`,
          sub: `${Math.round(b.attendance_avg)}% attendance · ${b.orgName}`,
          cta: 'Review', onClick: () => goToStudents(b.id),
        });
      }
      if (b.pending_grading > 0) {
        items.push({
          key: `grade-${b.id}`, kind: 'grade', score: 500 + b.pending_grading,
          title: `${b.pending_grading} submission${b.pending_grading === 1 ? '' : 's'} awaiting grading`,
          sub: `${b.name} · ${b.orgName}`,
          cta: 'Grade', onClick: () => goToOverview(b.id),
        });
      }
    });
    if (data.unanswered_questions > 0) {
      items.push({
        key: 'questions', kind: 'questions', score: 100 + data.unanswered_questions,
        title: `${data.unanswered_questions} unanswered question${data.unanswered_questions === 1 ? '' : 's'} from students`,
        sub: 'Awaiting your reply',
        cta: 'Answer', onClick: () => navigate({ to: '/trainer/questions' }),
      });
    }
    return items.sort((a, b) => b.score - a.score);
  }, [data, flatBatches]); // eslint-disable-line react-hooks/exhaustive-deps

  const FEED_LIMIT = 5;
  const visibleFeed = feed.slice(0, FEED_LIMIT);

  const sortedBatches = useMemo(() => {
    const arr = [...flatBatches];
    switch (sort) {
      case 'progress':   return arr.sort((a, b) => a.progress_pct - b.progress_pct);
      case 'attendance': return arr.sort((a, b) => a.attendance_avg - b.attendance_avg);
      case 'name':       return arr.sort((a, b) => a.name.localeCompare(b.name));
      default:           return arr.sort((a, b) =>
        (b.at_risk_count * 10 + b.pending_grading) - (a.at_risk_count * 10 + a.pending_grading));
    }
  }, [flatBatches, sort]);

  const nextSessionId = data?.today_sessions[0]?.id;

  if (isLoading) {
    return (
      <div className="space-y-5">
        <Skeleton className="h-9 w-72" />
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-[104px] rounded-lg" />)}
        </div>
        <div className="grid gap-4 lg:grid-cols-[1.55fr_1fr]">
          <Skeleton className="h-64 rounded-lg" />
          <Skeleton className="h-64 rounded-lg" />
        </div>
        <Skeleton className="h-72 rounded-lg" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex flex-col items-center py-16 text-center">
        <p className="text-muted-foreground">Couldn&apos;t load your dashboard.</p>
        <Button className="mt-4" onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  const orgNames = data.orgs.map(o => o.org_name);
  const subtitle = orgNames.length > 0 ? orgNames.join(' · ') : 'No active batches';
  const activeBatches = flatBatches.length;
  const batchOpts: BatchOpt[] = flatBatches.map(b => ({ id: b.id, name: b.name }));

  const sortTabs: { key: BatchSort; label: string }[] = [
    { key: 'attention',  label: 'Needs attention' },
    { key: 'progress',   label: 'Progress' },
    { key: 'attendance', label: 'Attendance' },
    { key: 'name',       label: 'Name' },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title={greeting(data.trainer.name)}
        subtitle={subtitle}
        actions={
          batchOpts.length > 0 ? (
            <>
              <button
                onClick={() => setAnnounceOpen(true)}
                className="inline-flex items-center gap-2 rounded-[12px] border border-[#ececec] bg-white px-3.5 py-2 text-[13px] font-semibold text-[#6b7280] transition-colors hover:text-[#111827]"
              >
                <Megaphone className="h-4 w-4" /> Announce
              </button>
              <button
                onClick={() => setScheduleOpen(true)}
                className="inline-flex items-center gap-2 rounded-[12px] bg-[#ff5a1f] px-3.5 py-2 text-[13px] font-semibold text-white transition-colors hover:opacity-90"
              >
                <CalendarPlus className="h-4 w-4" /> Schedule session
              </button>
            </>
          ) : undefined
        }
      />

      {/* KPIs — mirror the Analytics page */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
        <KpiCard label="Students" value={data.stats.total_students}
          icon={<Users className="h-4 w-4 text-[#0f766e]" />} color="text-[#0f766e]" iconBg="bg-[#f0fdfa]" />
        <KpiCard label="Batches" value={data.stats.total_batches}
          icon={<Layers className="h-4 w-4 text-[#111827]" />} color="text-[#111827]" iconBg="bg-[#f3f4f6]"
          sub={<span>{activeBatches} active</span>} />
        <KpiCard label="Avg Attendance" value={`${Math.round(data.stats.attendance_avg)}%`}
          icon={<UserCheck className="h-4 w-4 text-[#111827]" />} color="text-[#111827]" iconBg="bg-[#f3f4f6]" />
        <KpiCard label="To Grade" value={data.stats.pending_grading}
          icon={<ClipboardList className="h-4 w-4 text-[#ff5a1f]" />} color="text-[#ff5a1f]" iconBg="bg-[#fff1ec]" />
        <KpiCard label="At Risk" value={data.at_risk_count} alert
          icon={<AlertTriangle className="h-4 w-4 text-[#ff5a1f]" />} color="text-[#ff5a1f]" iconBg="bg-[#fff1ec]"
          sub={<span>below 20% progress</span>} />
      </div>

      {/* Primary zone: attention feed + today's schedule */}
      <div className="grid gap-4 lg:grid-cols-[1.55fr_1fr]">

        <TrainerCard>
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <h3 className="flex items-center gap-2 text-[14px] font-semibold text-foreground">
              Needs your attention
              {feed.length > 0 && (
                <span className="rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-bold text-red-600">
                  {feed.length}
                </span>
              )}
            </h3>
            <span className="text-[11px] text-muted-foreground">sorted by urgency</span>
          </div>
          {feed.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12 text-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-50">
                <UserCheck className="h-5 w-5 text-green-600" />
              </div>
              <p className="text-sm font-medium text-foreground">You&apos;re all caught up</p>
              <p className="text-[12.5px] text-muted-foreground">No grading, at-risk students, or open questions right now.</p>
            </div>
          ) : (
            <>
              <div className="pl-0">
                {visibleFeed.map(item => <FeedRow key={item.key} item={item} />)}
              </div>
              {feed.length > FEED_LIMIT && (
                <button
                  onClick={() => navigate({ to: '/trainer/analytics' })}
                  className="w-full border-t border-border px-5 py-2.5 text-center text-[12.5px] font-semibold text-[#ff5a1f] hover:bg-muted/40"
                >
                  {feed.length - FEED_LIMIT} more — view all
                </button>
              )}
            </>
          )}
        </TrainerCard>

        <TrainerCard>
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <h3 className="text-[14px] font-semibold text-foreground">Today&apos;s schedule</h3>
            <span className="text-[11px] text-muted-foreground">{data.today_sessions.length} sessions</span>
          </div>
          {data.today_sessions.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12 text-center">
              <Calendar className="h-7 w-7 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">No sessions scheduled for today.</p>
            </div>
          ) : (
            <div className="py-2">
              {data.today_sessions.map(s => (
                <ScheduleItem key={s.id} session={s} isNext={s.id === nextSessionId} onClick={() => goToOverview(s.batch_id)} />
              ))}
            </div>
          )}
        </TrainerCard>
      </div>

      {/* Reference zone: batch comparison */}
      {flatBatches.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">No active batches assigned to you yet.</p>
        </div>
      ) : (
        <TrainerCard>
          <div className="flex flex-wrap items-center gap-3 border-b border-border px-5 py-4">
            <h3 className="text-[14px] font-semibold text-foreground">Your batches</h3>
            <div className="ml-auto inline-flex rounded-lg bg-muted p-1">
              {sortTabs.map(t => (
                <button
                  key={t.key}
                  onClick={() => setSort(t.key)}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-[12px] font-semibold transition-colors',
                    sort === t.key ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-border">
                  {['Batch', 'Students', 'Progress', 'Attendance', 'At risk', 'To grade'].map((h, i) => (
                    <th key={h} className={cn(
                      'px-5 py-2.5 text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground',
                      i === 0 ? 'text-left' : 'text-right',
                    )}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedBatches.map(b => {
                  const pct = Math.round(b.progress_pct);
                  const barColor = pct < 20 ? 'bg-red-500' : pct < 75 ? 'bg-blue-500' : 'bg-green-500';
                  return (
                    <tr
                      key={b.id}
                      onClick={() => goToOverview(b.id)}
                      className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-muted/40"
                    >
                      <td className="px-5 py-3">
                        <div className="text-[13px] font-semibold text-foreground">{b.name}</div>
                        <div className="text-[11.5px] text-muted-foreground">{b.orgName}</div>
                      </td>
                      <td className="px-5 py-3 text-right text-[13px] tabular-nums text-muted-foreground">{b.student_count}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-2.5">
                          <div className="h-1.5 w-20 overflow-hidden rounded-full bg-muted">
                            <div className={`h-full rounded-full ${barColor}`} style={{ width: `${Math.min(100, pct)}%` }} />
                          </div>
                          <span className="w-9 text-right text-[12px] tabular-nums text-muted-foreground">{pct}%</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-right text-[12px] tabular-nums text-muted-foreground">{Math.round(b.attendance_avg)}%</td>
                      <td className="px-5 py-3 text-right"><Chip n={b.at_risk_count} tone="risk" /></td>
                      <td className="px-5 py-3 text-right"><Chip n={b.pending_grading} tone="warn" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TrainerCard>
      )}

      {batchOpts.length > 0 && (
        <>
          <ScheduleSessionDialog batches={batchOpts} open={scheduleOpen} onOpenChange={setScheduleOpen} />
          <AnnounceDialog batches={batchOpts} open={announceOpen} onOpenChange={setAnnounceOpen} />
        </>
      )}
    </div>
  );
}
